import React, { useState, useRef, useEffect } from "react";
import "./SearchCarBySpecPage.css";
import Navbar from "./Navbar";
import Title from "./Title";
import { JsonView, defaultStyles, allExpanded } from "react-json-view-lite";
import { ApiService } from "./services/api";
import CarTile from "./CarTile";
import { CarDto } from "./dto/car-dto";
import { FragmentFromSocketDto } from "./dto/fragment-dto";
import { io } from "socket.io-client";
import { useAuth } from "./provider/AuthContext";

/** ---- Shared types (gleich mit deinem Backend) ---- */
interface FieldMetadata {
  type: "range" | "enum" | "boolean" | "string";
  dataType: "number" | "string" | "boolean";
  options?: string[];
  min?: number;
  max?: number;
}
interface CarSpecField {
  currentValue: any;
  searchCriteria: FieldMetadata;
}
interface CarSpecsResponse {
  specs: Record<string, CarSpecField>;
}
interface RangeFilter {
  min?: number;
  max?: number;
}
interface SearchCriteria {
  brand?: string;
  model?: string;
  priceRange?: RangeFilter;
  interiorMaterial?: string | string[]; // <— allow multi
  transmissionType?: string | string[]; // <— allow multi
  yearRange?: RangeFilter;
  mileageInKmRange?: RangeFilter;
  hasNavigation?: boolean;
}

/** ---- Hilfsfunktionen ---- */
const isNumber = (v: any) => v !== "" && !Number.isNaN(Number(v));

const toArray = (v: unknown): string[] =>
  Array.isArray(v) ? v : v ? [String(v)] : [];

const getMultiSelectValues = (select: HTMLSelectElement): string[] =>
  Array.from(select.selectedOptions).map((o) => o.value);

/** Mappt ein Feld wie "year" -> "yearRange", "mileageInKm" -> "mileageInKmRange", "price" -> "priceRange" */
const rangeKeyMap = (key: string): keyof SearchCriteria => {
  if (key === "year") return "yearRange";
  if (key === "mileageInKm") return "mileageInKmRange";
  if (key === "price") return "priceRange";
  // Fallback: erlaubt generische Ranges, falls später mehr Felder kommen
  return `${key}Range` as keyof SearchCriteria;
};

const SearchCarBySpecPage: React.FC = () => {
  const { token, refreshToken } = useAuth();

  const [carSpecs, setCarSpecs] = useState<CarSpecsResponse | null>(null);
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({});
  const [searchResults, setSearchResults] = useState<CarDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"specs" | "search">("specs");
  const urlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const socket = io(
      process.env.REACT_APP_SOCKET_URL || "http://localhost:3000",
      {
        auth: {
          token,
        },
      }
    );

    socket.on("fragment", updateFragment);

    return () => {
      socket.off("fragment", updateFragment);
    };
  }, []);

  const updateFragment = (fragment: FragmentFromSocketDto) => {
    if (!fragment) return;

    setSearchResults((previous) => {
      return previous.map((car) => {
        if (car.externalCarId === fragment.data.externalCarId) {
          return { ...car, ...fragment.data };
        } else {
          return car;
        }
      });
    });
  };

  /** ---- Backend: Car Specs laden ---- */
  const loadCarSpecs = async () => {
    const url = urlInputRef.current?.value?.trim();
    if (!url) {
      setError("Bitte eine Auto-URL eingeben");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await ApiService.getCarSpecsWithSearchCriteria(url);
      setCarSpecs(response);
      setActiveTab("specs");
      // Optional: Suchkriterien zurücksetzen, wenn neues Auto geladen wurde
      setSearchCriteria({});
      setSearchResults([]);
    } catch (e) {
      setError("Car Specs konnten nicht geladen werden");
      // console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    prefillSearchCriteria();
  }, [carSpecs]);

  useEffect(() => {
    // Go through all results and send request to get www.makkizentrale.de/api/parser/emit-car-details
    searchResults.forEach((car) => {
      ApiService.emitCarDetails(car);
    });
  }, [searchResults]);

  const normalizeList = (v: unknown): string[] =>
    Array.isArray(v)
      ? v
          .map(String)
          .map((s) => s.trim())
          .filter(Boolean)
      : String(v ?? "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

  const prefillSearchCriteria = () => {
    if (!carSpecs) return;

    // Brand/Model vorfüllen
    if (carSpecs.specs.brand?.currentValue) {
      setSearchCriteriaField("brand", carSpecs.specs.brand.currentValue);
    }
    if (carSpecs.specs.model?.currentValue) {
      setSearchCriteriaField("model", carSpecs.specs.model.currentValue);
    }

    // Leder-Gruppe: Wenn ein Wert aus der Gruppe erkannt wird → alle 3 setzen
    const leatherGroup = new Set(["Vollleder", "Teilleder", "Alcantara"]);
    const rawIM = carSpecs.specs.interiorMaterial?.currentValue;
    const currentIM = normalizeList(rawIM);

    if (currentIM.some((v) => leatherGroup.has(v))) {
      setSearchCriteria((prev) => ({
        ...prev,
        interiorMaterial: Array.from(leatherGroup), // ["Volleder","Teilleder","Alcantara"]
      }));
    }

    const transmissionGroup = new Set(["Automatik"]);
    const rawTM = carSpecs.specs.transmissionType?.currentValue;
    const currentTM = normalizeList(rawTM);

    if (currentTM.some((v) => transmissionGroup.has(v))) {
      setSearchCriteria((prev) => ({
        ...prev,
        transmissionType: Array.from(transmissionGroup), // ["Automatik"]
      }));
    }

    if (carSpecs.specs.allWheelDrive?.currentValue === true) {
      setSearchCriteria((prev) => ({
        ...prev,
        allWheelDrive: true,
      }));
    }
  };

  /** ---- Backend: Ähnliche Autos suchen ---- */
  const searchSimilarCars = async () => {
    if (!carSpecs) {
      setError("Bitte zuerst Car Specs laden");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await ApiService.findCarsBySpecifications(
        searchCriteria
      );
      setSearchResults(response.cars || []);
      setActiveTab("search");
    } catch (e) {
      setError("Suche nach ähnlichen Autos fehlgeschlagen");
      // console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /** ---- Updater für einzelne Feldtypen ---- */
  const setSearchCriteriaField = (
    field: keyof SearchCriteria,
    value: string
  ) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [field]: value === "" ? undefined : value,
    }));
  };

  const updateEnum = (field: keyof SearchCriteria, value: string) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [field]: value === "" ? undefined : value,
    }));
  };

  const updateBoolean = (field: keyof SearchCriteria, value: string) => {
    setSearchCriteria((prev) => ({
      ...prev,
      [field]: value === "" ? undefined : value === "true",
    }));
  };

  const updateRange = (
    fieldKey: string,
    subKey: keyof RangeFilter,
    value: string,
    meta?: FieldMetadata
  ) => {
    const mappedKey = rangeKeyMap(fieldKey);
    setSearchCriteria((prev) => {
      const prevRange = (prev[mappedKey] as RangeFilter) || {};
      let num: number | undefined = undefined;
      if (isNumber(value)) {
        num = Number(value);
        if (meta?.min !== undefined) num = Math.max(meta.min, num);
        if (meta?.max !== undefined) num = Math.min(meta.max, num);
      }
      const next = { ...prevRange, [subKey]: num };
      // leere Range rauswerfen
      if (next.min === undefined && next.max === undefined) {
        const { [mappedKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [mappedKey]: next };
    });
  };

  const clearSearchCriteria = () => setSearchCriteria({});

  const getPlaceholderByKey = (key: string) => {
    switch (key) {
      case "brand":
        return "z. B. Audi";
      case "model":
        return "z. B. A4";
    }
  };

  /** ---- Renderer für die Eingabefelder je Spec ---- */
  const renderInputForSpec = (fieldKey: string, spec: CarSpecField) => {
    const meta = spec.searchCriteria;

    // String
    if (meta.type === "string") {
      const criteriaKey = fieldKey as keyof SearchCriteria;

      return (
        <input
          className="scbsp-input"
          type="text"
          placeholder={getPlaceholderByKey(fieldKey)}
          value={(searchCriteria[criteriaKey] as string) ?? ""}
          onChange={(e) => setSearchCriteriaField(criteriaKey, e.target.value)}
        />
      );
    }

    // Enum
    if (meta.type === "enum") {
      const criteriaKey = fieldKey as keyof SearchCriteria;
      const current = searchCriteria[criteriaKey];
      const selected = toArray(current); // normalize string | string[] -> string[]

      const onChangeMulti = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const values = getMultiSelectValues(e.target);
        setSearchCriteria((prev) => ({
          ...prev,
          [criteriaKey]:
            values.length === 0
              ? undefined
              : values.length === 1
              ? values[0] // keep single as string
              : values, // multi -> string[]
        }));
      };

      return (
        <div className="scbsp-multiselect">
          <select
            className="scbsp-input"
            multiple
            size={Math.min(4, meta.options?.length ?? 4)} // compact height
            value={selected}
            onChange={onChangeMulti}
          >
            {(meta.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <div className="scbsp-multiselect__hint">
            Ctrl + Klick für Mehrfachauswahl
          </div>
        </div>
      );
    }

    // Boolean
    if (meta.type === "boolean") {
      const criteriaKey = fieldKey as keyof SearchCriteria;
      const val = searchCriteria[criteriaKey];
      return (
        <select
          className="scbsp-input"
          value={val === undefined ? "" : (val as boolean) ? "true" : "false"}
          onChange={(e) => updateBoolean(criteriaKey, e.target.value)}
        >
          <option value="">– egal –</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      );
    }

    // Range
    if (meta.type === "range") {
      const mappedKey = rangeKeyMap(fieldKey);
      const range = (searchCriteria[mappedKey] as RangeFilter) || {};

      return (
        <div className="scbsp-range">
          <div className="scbsp-range-label">min:</div>
          <div className="scbsp-input">
            <input
              type="number"
              inputMode="numeric"
              placeholder={meta.min !== undefined ? `${meta.min}` : "min"}
              value={range.min ?? ""}
              onChange={(e) =>
                updateRange(fieldKey, "min", e.target.value, meta)
              }
            />
          </div>

          <div className="scbsp-range-label">max:</div>
          <div className="scbsp-input">
            <input
              type="number"
              inputMode="numeric"
              placeholder={meta.max !== undefined ? `${meta.max}` : "max"}
              value={range.max ?? ""}
              onChange={(e) =>
                updateRange(fieldKey, "max", e.target.value, meta)
              }
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const translateKeyToGerman = (key: string) => {
    switch (key) {
      case "brand":
        return "Marke";
      case "model":
        return "Modell";
      case "price":
        return "Preis";
      case "year":
        return "Baujahr";
      case "bodyType":
        return "Fahrzeugart";
      case "hasNavigation":
        return "Hat Navigation";
      case "interiorMaterial":
        return "Innenausstattung";
      case "mileageInKm":
        return "Kilometerstand";
      case "transmissionType":
        return "Getriebeart";
      case "allWheelDrive":
        return "Allradantrieb";
      case "powerInHp":
        return "Leistung in PS";
      default:
        return key;
    }
  };

  const convertValueToString = (value: any) => {
    switch (value) {
      case true:
        return "Ja";
      case false:
        return "Nein";
      case undefined:
        return "-";
      default:
        return value;
    }
  };

  /** ---- UI ---- */
  return (
    <div className="scbsp">
      <Title title="Ähnliche Autos 🚙 ~ 🚗" />
      <div className="scbsp__bar">
        <button
          // className="scbsp__btn"
          onClick={loadCarSpecs}
          disabled={loading}
        >
          Spezifikationen laden
        </button>
        <input
          ref={urlInputRef}
          type="text"
          className="scbsp__url"
          placeholder="Fahrzeug-URL einfügen …"
        />
      </div>

      <Navbar />

      {error && <div className="scbsp__error">{error}</div>}
      {loading && <div className="scbsp__loading">Lädt …</div>}

      {/* Tabs */}
      <div className="scbsp__tabs">
        <button
          className={`scbsp__tab ${activeTab === "specs" ? "is-active" : ""}`}
          onClick={() => setActiveTab("specs")}
        >
          Spezifikationen & Filter
        </button>
        <button
          className={`scbsp__tab ${activeTab === "search" ? "is-active" : ""}`}
          onClick={() => setActiveTab("search")}
        >
          Suchergebnisse
        </button>
      </div>

      {/* SPECS + FILTER */}
      {activeTab === "specs" && (
        <div className="scbsp__panel">
          {!carSpecs && (
            <div className="scbsp__hint">
              Bitte eine Fahrzeug-URL eingeben und Spezifikationen laden.
            </div>
          )}

          {carSpecs && (
            <>
              <div className="scbsp__grid">
                <div className="scbsp__gridHeader">Feld</div>
                <div className="scbsp__gridHeader">Aktueller Wert</div>
                <div className="scbsp__gridHeader">Suchkriterium</div>

                {Object.entries(carSpecs.specs).map(([key, spec]) => (
                  <React.Fragment key={key}>
                    <div className="scbsp__cell scbsp__field">
                      {translateKeyToGerman(key)}
                    </div>
                    <div className="scbsp__cell scbsp__value">
                      {convertValueToString(spec.currentValue)}
                    </div>
                    <div className="scbsp__cell scbsp__control">
                      {renderInputForSpec(key, spec)}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {/* Aktive Kriterien als JSON (optional für Debug/Transparenz) */}
              <div className="scbsp__json">
                <h4>Aktive Suchkriterien</h4>
                <JsonView
                  data={searchCriteria}
                  shouldExpandNode={allExpanded}
                  style={defaultStyles}
                />
              </div>

              <div className="scbsp__actions">
                <button className="scbsp__btn" onClick={clearSearchCriteria}>
                  Zurücksetzen
                </button>
                {/* // prefill search criteria with brand/model if available */}

                <button
                  className="scbsp__btn"
                  onClick={searchSimilarCars}
                  disabled={loading}
                >
                  Ähnliche Autos finden
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* SEARCH RESULTS */}
      {activeTab === "search" && (
        <div className="scbsp__panel">
          <div className="scbsp__resultsHead">
            Ergebnisse: {searchResults.length}
          </div>
          {searchResults.length === 0 && (
            <div className="scbsp__hint">Keine Treffer gefunden.</div>
          )}

          <div className="scbsp__cards">
            {searchResults.map((car: any, idx: number) => (
              <CarTile key={car._id ?? idx} car={car} isDuplicate={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchCarBySpecPage;
